/**
 * Pinned bottom sidebar sections: Company, Community, and Help.
 * Always visible at the bottom of the sidebar, separated by a border from the
 * scrollable nav area above.
 */

import { Box, Collapse, List, Typography } from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Chat as ChatIcon,
  Gavel as GavelIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  PlayCircleOutline as TourIcon,
} from '@mui/icons-material';
import { SidebarNavItem, SectionHeader } from '../SidebarNavItem';
import { COLORS } from '../layoutConstants';

interface SidebarBottomNavProps {
  sidebarExpanded: boolean;
  isAdmin: boolean;
  isRegulator: boolean;
  hasCompany: boolean;
  userRole: string;
  isSectionOpen: (label: string) => boolean;
  toggleSection: (label: string) => void;
  onMobileNavClick: () => void;
}

export function SidebarBottomNav({
  sidebarExpanded,
  isAdmin,
  isRegulator,
  hasCompany,
  userRole,
  isSectionOpen,
  toggleSection,
  onMobileNavClick,
}: SidebarBottomNavProps) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: `1px solid ${COLORS.sidebarBorder}`,
        background: 'linear-gradient(180deg, rgba(28, 36, 52, 0) 0%, rgba(16, 20, 30, 0.4) 100%)',
        pt: 0.5,
        pb: 1.5,
        overflowY: 'auto',
        maxHeight: '40%',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: COLORS.sidebarBorder,
          borderRadius: 2,
        },
      }}
    >
      <List component="nav" disablePadding>
        {/* Company section (all non-admin authenticated users + regulator) */}
        {(hasCompany || isRegulator) && (
          <>
            <SectionHeader
              label="Company"
              collapsible
              open={isSectionOpen('Company')}
              onToggle={() => toggleSection('Company')}
              sidebarExpanded={sidebarExpanded}
            />
            <Collapse in={sidebarExpanded ? isSectionOpen('Company') : true} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <SidebarNavItem
                  to="/company-info"
                  label="Company Info"
                  icon={<BusinessIcon />}
                  sidebarExpanded={sidebarExpanded}
                  onNavigate={onMobileNavClick}
                />
                {!isRegulator && (
                  <>
                    <SidebarNavItem
                      to="/business-classes"
                      label="Business Classes"
                      icon={<CategoryIcon />}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                    <SidebarNavItem
                      to="/contract-groups"
                      label="Contract Groups"
                      icon={<AccountTreeIcon />}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                    <SidebarNavItem
                      to="/ifrs17-policies"
                      label="IFRS 17 Policies"
                      icon={<GavelIcon />}
                      sidebarExpanded={sidebarExpanded}
                      onNavigate={onMobileNavClick}
                    />
                  </>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* Community section (all authenticated users) */}
        <SectionHeader
          label="Community"
          collapsible
          open={isSectionOpen('Community')}
          onToggle={() => toggleSection('Community')}
          sidebarExpanded={sidebarExpanded}
        />
        <Collapse in={sidebarExpanded ? isSectionOpen('Community') : true} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <SidebarNavItem
              to="/profile"
              label="My Profile"
              icon={<PersonIcon />}
              sidebarExpanded={sidebarExpanded}
              onNavigate={onMobileNavClick}
            />
            <SidebarNavItem
              to="/directory"
              label="Directory"
              icon={<PeopleIcon />}
              sidebarExpanded={sidebarExpanded}
              onNavigate={onMobileNavClick}
            />
            <SidebarNavItem
              to="/messages"
              label="Messages"
              icon={<ChatIcon />}
              sidebarExpanded={sidebarExpanded}
              onNavigate={onMobileNavClick}
            />
          </List>
        </Collapse>

        {/* Help & Reference section (non-admin users only) */}
        {!isAdmin && (
          <>
            <SectionHeader
              label="Help"
              collapsible
              open={isSectionOpen('Help')}
              onToggle={() => toggleSection('Help')}
              sidebarExpanded={sidebarExpanded}
            />
            <Collapse in={sidebarExpanded ? isSectionOpen('Help') : true} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {(userRole === 'consultant_user' || userRole === 'super_admin') && (
                  <SidebarNavItem
                    to="/act-engine-manual"
                    label="Engine Manual"
                    icon={<MenuBookIcon />}
                    sidebarExpanded={sidebarExpanded}
                    onNavigate={onMobileNavClick}
                  />
                )}
                <SidebarNavItem
                  to="/glossary"
                  label="IFRS 17 Glossary"
                  icon={<MenuBookIcon />}
                  sidebarExpanded={sidebarExpanded}
                  onNavigate={onMobileNavClick}
                />
                <Box
                  onClick={() => {
                    localStorage.removeItem('ifrs17_walkthrough_completed');
                    window.location.reload();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    mx: 1,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  <TourIcon sx={{ color: COLORS.navText, fontSize: 20 }} />
                  {sidebarExpanded && (
                    <Typography sx={{ fontSize: 13, color: COLORS.navText }}>
                      Restart Walkthrough
                    </Typography>
                  )}
                </Box>
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );
}
